import MaterialIcon from '@components/common/MaterialIcon';
import { FolderSelectionSection } from '@components/data-sources/sections/FolderSelectionSection';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { useDataSourceActions } from '@hooks/dataSources/useDataSourceActions';
import { useDataSourceFolderActions } from '@hooks/dataSources/useDataSourceFolderActions';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useFolderSelection } from '@hooks/dataSources/useFolderSelection';
import { useModal } from '@hooks/modal/useModal';
import { format } from 'date-fns';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const MoveDataSource: React.FC = () => {
  const { selectedDataSourceFile } = useDataSources();
  const { moveDataSource } = useDataSourceActions();
  const { fetchDataSourceFolders } = useDataSourceFolderActions();
  const { hideModal } = useModal();
  const { t } = useTranslation();

  const [expiryDate, setExpiryDate] = useState<Date | null>(
    selectedDataSourceFile?.expiryDate
      ? new Date(selectedDataSourceFile.expiryDate)
      : null,
  );

  const {
    folders,
    currentFolderId,
    currentSubfolderId,
    currentFolderName,
    currentSubfolderName,
    selectedFolderId,
    selectedSubfolderId,
    setSelectedFolderId,
    setSelectedSubfolderId,
    resolvedFolderId,
    isTargetTemporary,
  } = useFolderSelection(selectedDataSourceFile?.folderId?.toString());

  const isSameLocation =
    selectedFolderId === currentFolderId &&
    selectedSubfolderId === currentSubfolderId;

  const isMoveDisabled =
    !selectedFolderId || isSameLocation || (isTargetTemporary && !expiryDate);

  const handleMove = async () => {
    if (!selectedDataSourceFile?._id || isMoveDisabled) {
      return;
    }

    await moveDataSource(selectedDataSourceFile._id?.toString(), {
      folderId: resolvedFolderId ?? null,
      expiryDate: isTargetTemporary
        ? expiryDate
          ? format(expiryDate, 'yyyy-MM-dd')
          : null
        : null,
    });

    fetchDataSourceFolders();

    hideModal();
  };

  if (!selectedDataSourceFile) {
    return null;
  }

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          {/* Description */}
          <div className='col-span-1'>
            <p className='text-sm'>
              {t('data-sources.move.description', {
                fileName: selectedDataSourceFile.name,
              })}
            </p>
          </div>

          {/* Current location info */}
          {(currentFolderName || currentSubfolderName) && (
            <div className='col-span-1 p-3 bg-blue-50 border border-blue-200 rounded-md'>
              <p className='text-xs font-semibold text-blue-900 mb-1 flex items-center gap-1'>
                <MaterialIcon
                  icon='info'
                  classes={['text-sm', 'leading-none']}
                />
                {t('data-sources.move.current-location')}
              </p>
              <p className='text-sm text-blue-800'>
                {currentFolderName}
                {currentSubfolderName ? ` › ${currentSubfolderName}` : ''}
              </p>
            </div>
          )}

          <div className='tailwind-divider-x' />

          {/* Folder + subfolder selection */}
          <div className='col-span-1'>
            <FolderSelectionSection
              folders={folders}
              selectedFolderId={selectedFolderId}
              selectedSubfolderId={selectedSubfolderId}
              onFolderChange={setSelectedFolderId}
              onSubfolderChange={setSelectedSubfolderId}
              expiryDate={expiryDate}
              onExpiryDateChange={setExpiryDate}
              isTargetTemporary={isTargetTemporary}
            />
          </div>
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleMove}
          disabled={isMoveDisabled}>
          {t('data-sources.move.confirm-button')}
        </ButtonT>
      </div>
    </div>
  );
};

export default MoveDataSource;
